import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '@/store/auth';
import { AuthContextType } from '@/types/auth';
import { logger } from '@/utils/logger';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    updateUser,
    clearError,
    checkAuth,
  } = useAuthStore();

  // Check authentication status on mount
  useEffect(() => {
    // 초기 로드 시에만 인증 체크
    const performAuthCheck = async () => {
      try {
        await checkAuth();
      } catch (error) {
        logger.error('Initial auth check failed:', error);
        // 인증 체크 실패 시에도 앱이 계속 작동하도록 함
      }
    };
    
    performAuthCheck();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Log authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      logger.userAction('User authenticated', {
        userId: user.id,
        email: user.email,
        role: user.role,
      });
    }
  }, [isAuthenticated, user]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    updateUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType & { token?: string | null } => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Add token from localStorage for backward compatibility
  const token = localStorage.getItem('access_token');
  
  return {
    ...context,
    token
  };
};